import { CalendarCheck, MapPin, Timer } from 'lucide-react';
import React from 'react';

import { KpiGrid } from '../KpiCard';
import { PivotTable } from '../PivotTable';
import { ReportKpiCard } from '../report-cards';
import { LegacyTable, ReportSection } from '../ReportSection';

import {
  makeDestinationPivotCell,
  num,
  TIMELINE_BUCKETS,
  topPivotColumn,
  withPct,
} from './band-helpers';

import type { BandProps } from './band-props';

export const BookingsBand = ({
  report,
  totals,
  scopedRow,
  scopedWon,
  loading,
  dash,
  agentEntries,
  agentCell,
  expandAllAction,
  drillCell,
  from,
  to,
  ownerId,
  destinationPivotData,
  destinationPivotLoading,
}: BandProps): React.JSX.Element => {
  // Top destination scoped to the header owner filter: for a picked owner, the
  // max destination in THAT owner's pivot row; otherwise the overall top.
  const scopedTopDestination = (): string => {
    const pivot = destinationPivotData;
    if (!pivot) return '-';
    if (!ownerId) return topPivotColumn(pivot);
    const ri = pivot.rowIds.indexOf(ownerId);
    const cells = ri >= 0 ? (pivot.cells[ri] ?? []) : [];
    const max = cells.length ? Math.max(...cells) : 0;
    return max > 0 ? (pivot.columns[cells.indexOf(max)] ?? '-') : '-';
  };
  const destinationPivotCell = makeDestinationPivotCell(destinationPivotData, drillCell, from, to);

  return (
    <div className="space-y-4 pt-2">
      <KpiGrid>
        <ReportKpiCard
          icon={CalendarCheck}
          tone="success"
          value={scopedRow ? num((scopedWon ?? 0) + scopedRow.carriedIn.currentWon) : dash}
          label="Bookings (Won)"
          hint={
            scopedRow
              ? `${num(scopedWon ?? 0)} current + ${num(scopedRow.carriedIn.currentWon)} carry fwd`
              : undefined
          }
        />
        <ReportKpiCard
          icon={Timer}
          tone="primary"
          value={scopedRow?.bookingTimeline['WITHIN_7_DAYS'] ?? dash}
          label="Booking Window ≤7 Days"
        />
        <ReportKpiCard
          icon={MapPin}
          tone="warning"
          value={scopedTopDestination()}
          label="Top Destination"
        />
      </KpiGrid>

      <ReportSection title="Booking Window (Travel Timeline)" actions={expandAllAction}>
        <LegacyTable
          loading={loading || !report}
          headers={['Agent', ...TIMELINE_BUCKETS.map((bucket) => bucket.label)]}
          rows={agentEntries.map(({ row, ...entry }) => {
            // % = this window's share of the agent's total bookings across all
            // windows, so the row shows how their bookings spread over time.
            const rowTotal = TIMELINE_BUCKETS.reduce(
              (sum, b) => sum + (row.bookingTimeline[b.code] ?? 0),
              0,
            );
            return [
              { node: agentCell({ row, ...entry }), sort: row.agentName },
              ...TIMELINE_BUCKETS.map((bucket) => {
                const count = row.bookingTimeline[bucket.code] ?? 0;
                return drillCell(
                  withPct(count, rowTotal),
                  count,
                  count,
                  `${row.agentName} - ${bucket.label}`,
                  {
                    ...(row.agentId ? { assignedAgentId: row.agentId } : {}),
                    bookingTimeline: bucket.code,
                    createdAtFrom: from,
                    createdAtTo: to,
                  },
                );
              }),
            ];
          })}
          totals={
            totals &&
            (() => {
              const grandTotal = TIMELINE_BUCKETS.reduce(
                (sum, b) => sum + (totals.bookingTimeline[b.code] ?? 0),
                0,
              );
              return [
                'Total',
                ...TIMELINE_BUCKETS.map((bucket) => {
                  const count = totals.bookingTimeline[bucket.code] ?? 0;
                  return drillCell(
                    withPct(count, grandTotal),
                    count,
                    count,
                    `All agents - ${bucket.label}`,
                    {
                      bookingTimeline: bucket.code,
                      createdAtFrom: from,
                      createdAtTo: to,
                    },
                  );
                }),
              ];
            })()
          }
        />
      </ReportSection>

      <PivotTable
        title="Destination Wise Query"
        pivot={destinationPivotData}
        loading={destinationPivotLoading}
        renderCell={destinationPivotCell}
      />
    </div>
  );
};
